import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { firstValueFrom, forkJoin, interval, Observable, ReplaySubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppSettings } from '../app-settings';
import { AppVersionService, AuthenticationService, FileDownloadMonitorService, Lookup, Option, PayrollCampus, Result, SubjectDictionary } from '../domain';
import { ActionProposal, AdHocView, CandidateFiles, CaseFiles, CaseId, CaseReviewDateOverridesResponse, ComprehensiveReviewRequirements, EligibilityListReportRow, PositionFacts, PrcCandidateCommittee, ProgramReviewSettings, ProgressionTable, PurePositionId, ReadOnlyCommitteeGroup, ReviewDateEntry, ReviewDatesResponse, ReviewerCase, SystemClockInfo, AHRService as svc } from '../domain/data-access-interfaces/ahr.service';
import { ActionType, Case, CaseReconciliation, ChangeStep, ChangeTerm, CreateAcademic, HRAcademic, HRCommand, HRUnit, NewsStory, NewsStoryElementAcademic, ReconChange, SetAcademic, SetSupervisors, Supervisor, SupervisorDirectReports, TermId, Title } from '../domain/types/ahr';
import { ConsoleService } from '../widgets/console.service';

@Injectable()
export class AHRService implements svc {
    private _signalRConnection: signalR.HubConnection | null = null;
    private _signalRWatchdog: Subscription | null = null;
    private caseSubject =
        new ReplaySubject<Case[]>(1);
    private _caseNewsSubjects = 
        new SubjectDictionary<string,NewsStory[],ReplaySubject<NewsStory[]>>(
            () => new ReplaySubject<NewsStory[]>(1));
    private newsSubject = new ReplaySubject<NewsStory[]>(1);

    private _titles: Title[] = [];
    private _reviewerCases: ReviewerCase[] | null = null;

    constructor(
        private http: HttpClient,
        private appSettings: AppSettings,
        private readonly console: ConsoleService,
        private readonly authSvc: AuthenticationService,
        private readonly fileDownloadMonitorService: FileDownloadMonitorService,
        private readonly appVersion: AppVersionService,
        private readonly ngZone: NgZone
    ){}

    // Which review cycle is current is the server's to decide — it owns the rollover
    // cutoff. Passing one from here would let a stale tooltip cache put this client on
    // a different cycle than the server and every other caller.
    public getCurrentPeriodOfReviewYear(): Promise<string> {
        return firstValueFrom(this.http.get<string>(this.appSettings.APIURL + '/program-review/current_period_of_review_year'));
    }

    public getCasesToBeReviewedCurrentUser(ahryear?: string): Promise<ReviewerCase[]> {

        let queryParams = new HttpParams();

        if (ahryear)
            queryParams = queryParams.set('ahryear', ahryear.toString());

        return firstValueFrom(this.http.get<any[]>(this.appSettings.APIURL + '/program-review/reviewer_cases', { params: queryParams })
        .pipe(map(cases => {
            var cases_ = cases.map(ReviewerCase.fromJson);

            this._reviewerCases = cases_;
            this.console.log("cases", cases_);

            return cases_;

        })));
    }

    public getCaseToBeReviewedByWorkflowCaseId(workflowCaseId: number): Promise<Option<ReviewerCase>> {
        if (this._reviewerCases !== null && this._reviewerCases.length > 0)
            return Promise.resolve(Option.create(this._reviewerCases.find(c => c.id.workflowCaseId === workflowCaseId)));

        return this.getCasesToBeReviewedCurrentUser().then(cs => Option.create(cs.find(c => c.id.workflowCaseId === workflowCaseId)));
    }

    public getCaseFiles(caseId: CaseId, userId: number): Observable<CaseFiles>{
        let apiUrl = this.appSettings.APIURL + `/program-review/case/${caseId.academicId}/workflow-files/${caseId.periodOfReviewEndingInYear}`;
        return forkJoin({
            files : this.http.get(apiUrl), // wfa-files
            fileDownloadEventArr : this.fileDownloadMonitorService.getFileDownloadEvent(caseId.periodOfReviewEndingInYear + 1, userId)
            // fileDownloadEventArr : of([
            //     { committeeName: "finalDecision", date: "2025-03-14T12:00:00Z" },
            //     { committeeName: "peerReviewCommittee", date: "2025-03-15T15:30:00Z" }
            // ])
        }).pipe(
            map(({files, fileDownloadEventArr}) => {

            //Test data
            // if (caseId.academicId == '**'){                
            //     var testCandidateFiles: ReviewFile[] = [
            //         new ReviewFile('a','Position Description',"")
            //         ,new ReviewFile('b','Cover Page',"")
            //         ,new ReviewFile('c','Project Board Export',"")
            //         ,new ReviewFile('d','Bibliography',"")
            //         ,new ReviewFile('e','Sabbatic Leave Report',"")
            //         ,new ReviewFile('f','Letters of Publication Acceptance',"")
            //         ,new ReviewFile('g','Work Plan or Performance Improvement Plan',"")
            //     ];
            //     var testReviewerFiles = [
            //         new ReviewFile('aa','Primary Supervisor Review')
            //         ,new ReviewFile('bb','Secondary Supervisor Review')
            //     ];
            //     var canReadReviewFilesFromCommitteeNames : string[];

            //     return new CaseFiles(testCandidateFiles, testReviewerFiles, canReadReviewFilesFromCommitteeNames);
            // }     

               return CaseFiles.fromJson(files, fileDownloadEventArr);
            })
        );
    }

    public caseFileObjectUrl(caseId: CaseId, fileId: string): Observable<any> {
        let url = this.appSettings.APIURL + `/program-review/case/workflow-attachments/${fileId}/download`;
        return this.http.get<any>(url, { responseType: "blob" as 'json',observe: 'response'})
        .pipe(map(response => {
            this.console.log(response);
            var filename = response.headers.get('Content-Disposition').split("=")[1];
            filename = filename.replace(/(^"|"$)/g, '');
            const blob = response.body;
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.download = filename;
            anchor.href = url;
            anchor.click();
            //return window.URL.createObjectURL(
            //    new File([response.body], filename, {type: response.body.type} ))
        }));
    }
    public caseFileObject(caseId: CaseId, fileId: string): Observable<any> {
        let url = this.appSettings.APIURL + `/program-review/case/workflow-attachments/${fileId}/download`;
        return this.http.get<any>(url, { responseType: "blob" as 'json', observe: 'response'});
    }
    public dossierFileObject(yearId: number, academicId: string, fileId: string): Observable<any> {
        let url = this.appSettings.APIURL + `/program-review/case/workflow-attachments/${fileId}/download`;
        return this.http.get<any>(url, { responseType: "blob" as 'json', observe: 'response'})
    }
    public config(): Promise<null> {
        return firstValueFrom(this.http.get<Title[]>(this.appSettings.APIURL + '/program-review/titles'))
        .then(titles => {
            this._titles = titles;
            this.console.log('Titles:', titles);
            return null;
        });
    }

    public getSettings(): Promise<ProgramReviewSettings[]> {
        return firstValueFrom(this.http.get<ProgramReviewSettings[]>(this.appSettings.APIURL + '/program-review/settings'));
    }

    public saveSettings(s: ProgramReviewSettings): Promise<Result<void,string>>{
        return firstValueFrom(this.http.put(this.appSettings.APIURL + '/program-review/settings', s))
        .then(o => Result.fromJsonFSharp(o, () => null, err => err));
    }

    public getReviewDateYears(): Promise<number[]> {
        return firstValueFrom(this.http.get<number[]>(this.appSettings.APIURL + '/program-review/review-dates/years'));
    }

    public getReviewDates(year: number): Promise<ReviewDatesResponse> {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + `/program-review/review-dates/${year}`))
        .then(ReviewDatesResponse.fromJson);
    }

    public saveReviewDates(year: number, dates: ReviewDateEntry[]): Promise<Result<void,string>> {
        return firstValueFrom(this.http.put(this.appSettings.APIURL + `/program-review/review-dates/${year}`, { dates }))
        .then(o => Result.fromJsonFSharp(o, () => {
            this.appVersion.notifyReviewDatesChanged();
            return null;
        }, err => err));
    }

    public cloneReviewDatesFrom(sourceYear: number, targetYear: number): Promise<Result<void,string>> {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/review-dates/${targetYear}/clone-from/${sourceYear}`, null))
        .then(o => Result.fromJsonFSharp(o, () => null, err => err));
    }

    public getReviewerCommitteeYears(): Promise<number[]> {
        return firstValueFrom(this.http.get<number[]>(this.appSettings.APIURL + '/program-review/reviewer-committees/years'));
    }

    public getPeerReviewCommittees(reviewYearId: number): Promise<PrcCandidateCommittee[]> {
        return firstValueFrom(this.http.get<PrcCandidateCommittee[]>(this.appSettings.APIURL + `/program-review/reviewer-committees/prc/${reviewYearId}`));
    }

    public getAdHocCommittees(reviewYearId: number): Promise<AdHocView> {
        return firstValueFrom(this.http.get<AdHocView>(this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}`));
    }

    // Committee write endpoints return an F#-style Result on success and a
    // BAD_REQUEST string on validation failure — surface both as Result.
    private committeeWrite(obs: Observable<any>): Promise<Result<void,string>> {
        return firstValueFrom(obs)
        .then(o => Result.fromJsonFSharp<void,string>(o, () => null as any, (err: string) => err))
        .catch(err => Result.failure<void,string>(
            typeof err?.error === 'string' && err.error ? err.error : 'Request failed'));
    }

    public upsertPrcReviewer(reviewYearId: number, candidateUserId: number, reviewerUserId: number, writesReview: boolean): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.post(
            this.appSettings.APIURL + `/program-review/reviewer-committees/prc/${reviewYearId}/candidate/${candidateUserId}/reviewer/${reviewerUserId}?writesReview=${writesReview}`, null));
    }

    public removePrcReviewer(reviewYearId: number, candidateUserId: number, reviewerUserId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/prc/${reviewYearId}/candidate/${candidateUserId}/reviewer/${reviewerUserId}`));
    }

    public createAdHocCommittee(reviewYearId: number): Promise<number> {
        return firstValueFrom(this.http.post<{ committeeId: number }>(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}`, null))
        .then(o => o.committeeId);
    }

    public deleteAdHocCommittee(reviewYearId: number, committeeId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}/committee/${committeeId}`));
    }

    public upsertAdHocMember(reviewYearId: number, committeeId: number, userId: number, writesReview: boolean): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.post(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}/committee/${committeeId}/member/${userId}?writesReview=${writesReview}`, null));
    }

    public removeAdHocMember(reviewYearId: number, committeeId: number, userId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}/committee/${committeeId}/member/${userId}`));
    }

    public assignAdHocCandidate(reviewYearId: number, committeeId: number, candidateUserId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.post(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}/committee/${committeeId}/case/${candidateUserId}`, null));
    }

    public unassignAdHocCandidate(reviewYearId: number, committeeId: number, candidateUserId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/adhoc/${reviewYearId}/committee/${committeeId}/case/${candidateUserId}`));
    }

    public getReadOnlyCommitteeGroups(reviewYearId: number): Promise<ReadOnlyCommitteeGroup[]> {
        return firstValueFrom(this.http.get<ReadOnlyCommitteeGroup[]>(
            this.appSettings.APIURL + `/program-review/reviewer-committees/readonly/${reviewYearId}`));
    }

    public addReadOnlyReviewer(reviewYearId: number, kind: 'prc'|'adhoc', userId: number, groupId?: number): Promise<Result<void,string>> {
        const groupParam = groupId !== undefined ? `?groupId=${groupId}` : '';
        return this.committeeWrite(this.http.post(
            this.appSettings.APIURL + `/program-review/reviewer-committees/readonly/${reviewYearId}/${kind}/user/${userId}${groupParam}`, null));
    }

    public removeReadOnlyReviewer(reviewYearId: number, kind: 'prc'|'adhoc', userId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/readonly/${reviewYearId}/${kind}/user/${userId}`));
    }

    public addReadOnlyCandidate(groupId: number, candidateUserId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.post(
            this.appSettings.APIURL + `/program-review/reviewer-committees/readonly/group/${groupId}/candidate/${candidateUserId}`, null));
    }

    public removeReadOnlyCandidate(groupId: number, candidateUserId: number): Promise<Result<void,string>> {
        return this.committeeWrite(this.http.delete(
            this.appSettings.APIURL + `/program-review/reviewer-committees/readonly/group/${groupId}/candidate/${candidateUserId}`));
    }

    public getCaseReviewDateOverrides(caseId: number): Promise<CaseReviewDateOverridesResponse> {
        return firstValueFrom(this.http.get<CaseReviewDateOverridesResponse>(this.appSettings.APIURL + `/program-review/case/${caseId}/review-date-overrides`));
    }

    public saveCaseReviewDateOverrides(caseId: number, dates: ReviewDateEntry[]): Promise<Result<void,string>> {
        return firstValueFrom(this.http.put(this.appSettings.APIURL + `/program-review/case/${caseId}/review-date-overrides`, { dates }))
        .then(o => Result.fromJsonFSharp(o, () => null, err => err));
    }

    public getSystemClock(): Promise<SystemClockInfo> {
        return firstValueFrom(this.http.get<SystemClockInfo>(this.appSettings.APIURL + '/program-review/system-clock'));
    }

    public setSystemClock(date: string): Promise<Result<void,string>> {
        return firstValueFrom(this.http.put<{ simulatedDate: string }>(this.appSettings.APIURL + '/program-review/system-clock', { date }))
        .then(res => {
            // Patch the acting client's own banner immediately. The SignalR
            // systemClockUpdated broadcast only reaches sessions inside the AHR
            // section, so the user setting the clock from elsewhere (e.g. the
            // banner modal on any page) would otherwise see no change until reload.
            this.patchSimulatedDate(res?.simulatedDate || undefined);
            this.appVersion.notifyClockChanged();
            return Result.success<void,string>(null);
        })
        .catch(err => Result.failure<void,string>(
            typeof err?.error === 'string' && err.error ? err.error : 'Failed to set the simulated date'));
    }

    public clearSystemClock(): Promise<void> {
        return firstValueFrom(this.http.delete(this.appSettings.APIURL + '/program-review/system-clock')).then(() => {
            this.patchSimulatedDate(undefined);
            this.appVersion.notifyClockChanged();
            return null;
        });
    }

    /** Update the simulated date on the once-fetched /version payload that backs
     *  the status banner, so the banner reflects a set/clear without a reload. */
    private patchSimulatedDate(simulatedDate: string | undefined) {
        if (this.appVersion.serverInfo)
            this.appVersion.serverInfo.simulatedDate = simulatedDate;
    }

    public denyProposedAdvancement(caseId: string, secondaryAction: any, actionType: ActionType): Promise<Result<null,string>>{
        let url = `/proposed-advancement/denial`;
        if(actionType === ActionType.FiveYearReview) url = `/proposed-fiveyearreview/denial`;
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/case/${caseId}${url}`, secondaryAction))
        .then(o => Result.fromJsonFSharp(o, _ => null, msg => msg));
    }

    public approveProposedAdvancement(caseId: string, yearId: number, actionType: ActionType): Promise<Result<null,string>>{
         let url = `/proposed-advancement/approval`;
        if(actionType === ActionType.FiveYearReview) url = `/proposed-fiveyearreview/approval`;
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/case/${caseId}${url}`, yearId))
        .then(o => Result.fromJsonFSharp(o, _ => null, msg => msg));
    }

    public getWorkflowTaskIdForReviewer(workflowCaseId: number): Promise<number | null> {
        return firstValueFrom(this.http.get<{taskId: number | null}>(
            this.appSettings.APIURL + `/program-review/case/${workflowCaseId}/workflow-task-id`
        )).then(r => r.taskId);
    }

    public getCandidateWorkflowFilesForCurrentUser(yearId: number, academicId: string): Observable<CandidateFiles>{
        let url = this.appSettings.APIURL + `/program-review/case/current_user/${yearId}/workflow-files`;
        
        return forkJoin({
            files : this.http.get<CandidateFiles>(url),  // wfa-files
            fileDownloadEventArr : this.fileDownloadMonitorService.getFileDownloadEvent(yearId)
            // fileDownloadEventArr : of([
            //     { committeeName: "finalDecision", date: "2025-03-14T12:00:00Z" },
            //     { committeeName: "peerReviewCommittee", date: "2025-03-15T15:30:00Z" }
            // ])
        }).pipe(
            map(({files, fileDownloadEventArr}) => {
                return CandidateFiles.fromJson(files, fileDownloadEventArr);
            })
        );
    }

    public dossierFileObjectUrl(yearId: number, fileId: string, academicId: string): Observable<any> {
        let url = this.appSettings.APIURL + `/program-review/case/workflow-attachments/${fileId}/download`;
        return this.http.get<any>(url, { responseType: "blob" as 'json', observe: 'response'})
        .pipe(map(response => {
            this.console.log(response);
            var filename = response.headers.get('Content-Disposition').split("=")[1];
            filename = filename.replace(/(^"|"$)/g, '');
            const blob = response.body;
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.download = filename;
            anchor.href = url;
            anchor.click();
            //return window.URL.createObjectURL(
            //    new File([response.body], filename, {type: response.body.type} ))
        }));
    }

    private alt = true;
    public downloadDossierFile(yearId: number, fileId: string,academicId: string): void {
        if(this.alt){
            this.dossierFileObjectUrl(yearId, fileId, academicId)
            .subscribe(objectUrl =>{
                downloadViaDomClick(objectUrl);
            });
        } else {
            this.http.get<string>(
                this.appSettings.APIURL + `/program-review/case/workflow-attachments/${fileId}/download`)
                .subscribe(url => window.open(url, '_blank'));
        }

		function downloadViaDomClick(url: string, filename: string = ''){
			let anchor = window.document.createElement('a');
            anchor.href = url;
            if(filename != ''){
                anchor.download = filename;
            }

			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);
		}
    }

    public appealResult(caseId: string): Promise<Result<null,string>>{
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/case/${caseId}/appeal`,null))
        .then(o => Result.fromJsonFSharp(o, _ => null, msg => msg));
    }

    public promoteSupervisor(id: string, userId: number): Promise<Result<null,string>>{
        return firstValueFrom(this.http.put(this.appSettings.APIURL + `/program-review/academic/${id}/supervisor/${userId}/promote`,{}))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg))
        .catch(err => Result.failure<null,string>(this.serverErrorMessage(err)));
    }
    public demoteSupervisor (id: string, userId: number): Promise<Result<null,string>>{
        return firstValueFrom(this.http.put(this.appSettings.APIURL + `/program-review/academic/${id}/supervisor/${userId}/demote`,{}))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg))
        .catch(err => Result.failure<null,string>(this.serverErrorMessage(err)));
    }
    public addSupervisor (id: string, userId: number, reviewParticipationId: string): Promise<Result<null,string>>{
        return firstValueFrom(this.http.put(this.appSettings.APIURL + `/program-review/academic/${id}/supervisor/${userId}/${reviewParticipationId}`, {}))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg))
        .catch(err => Result.failure<null,string>(this.serverErrorMessage(err)));
    }
    public removeSupervisor (id: string, userId: number): Promise<Result<null,string>>{
        return firstValueFrom(this.http.delete(this.appSettings.APIURL + `/program-review/academic/${id}/supervisor/${userId}`))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg))
        .catch(err => Result.failure<null,string>(this.serverErrorMessage(err)));
    }

    // These two intentionally do NOT use Result.fromJsonFSharp. The reconciliation
    // endpoints return the bare view object on success (no F# Success-union wrapper),
    // so fromJsonFSharp — which switches on `o.case` — would throw on the success path.
    // GET: success = the view (200), failure = a rejected request (404 → .catch).
    // POST apply: success = the view (200), failure = an F# Failure union {case,fields}
    // also at 200, so it is discriminated here by `o.case === 'Failure'`.
    public getCaseReconciliation(caseId: number): Promise<Result<CaseReconciliation, string>> {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + `/program-review/case/${caseId}/reconciliation`))
            .then(o => Result.success<CaseReconciliation, string>(o as CaseReconciliation))
            .catch(err => Result.failure<CaseReconciliation, string>(this.serverErrorMessage(err)));
    }

    public applyReconciliation(caseId: number, changes: ReconChange[]): Promise<Result<CaseReconciliation, string>> {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/case/${caseId}/reconciliation/apply`, { changes }))
            .then((o: any) =>
                o && o.case === 'Failure'
                    ? Result.failure<CaseReconciliation, string>(o.fields[0])
                    : Result.success<CaseReconciliation, string>(o as CaseReconciliation))
            .catch(err => Result.failure<CaseReconciliation, string>(this.serverErrorMessage(err)));
    }

    /** Pulls the {error} message out of a rejected HTTP request (e.g. a 400 from
     *  the supervisor-removal guard). HttpClient rejects on non-2xx, so these
     *  responses never reach the .then branch above. */
    private serverErrorMessage(err: any): string {
        return typeof err?.error === 'string' && err.error ? err.error : 'The request failed. Please try again.';
    }

    public removeCountyAssignment(id: string, countyId: number): Promise<Result<null,string>>{
        return firstValueFrom(this.http.delete(this.appSettings.APIURL + `/program-review/academic/${id}/county_assignment/${countyId}`))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg));
    }

    public addCountyAssignment(id: string, countyId: number): Promise<Result<null,string>>
    {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/academic/${id}/county_assignment/${countyId}`, null))
        .then(r => Result.fromJsonFSharp(r, _ => null, msg => msg));
    }

    public createNewAcademic(
        userId: number,
        startingPositionId: PurePositionId,
        term: Option<TermId>,
        startDate: Date
    ): Promise<Result<null,string>> {
        return firstValueFrom(this.http.post(
            this.appSettings.APIURL + '/program-review/academic',
            {
                userId: userId,
                positionId: startingPositionId,
                term: term.toJsonFSharp(t => ({case:t})),
                startDate: startDate
            })
        .pipe(map(json => Result.fromJsonFSharp(json, _ => null, str => str))));
    }

    public getProgressionTable(f: PositionFacts): Promise<Option<ProgressionTable>>{
        return firstValueFrom(this.http.get(
            this.appSettings.APIURL + '/program-review/progression_table',
            {params: <{[key:string]: any}>f})
        .pipe(map(json => Option.fromJsonFSharp(json,ProgressionTable.fromJson))));
    }

    public getReviewRequirements(titleId: number, rankId: number, stepId: number): Promise<ComprehensiveReviewRequirements>{
        return firstValueFrom(this.http.get(
            this.appSettings.APIURL + '/program-review/review_requirements',
            {params: <{[key:string]: any}>{ titleId, rankId, stepId }})
        .pipe(map(ComprehensiveReviewRequirements.fromJson)));
    }

    // Full single-case detail with fresh review-step projections. The /program-review/case
    // list payload is thin (no reviewSteps/committees) — detail views must fetch through here.
    public getCaseById(caseId: string): Promise<Option<Case>> {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + `/program-review/case/${caseId}`)
        .pipe(map(json => Option.fromJsonFSharp(json, Case.fromJson))));
    }

    public getCasesForCurrentUser(): Observable<Case[]> {
        return this.http.get(this.appSettings.APIURL + '/program-review/case/current_user/')
        .pipe(map(json => (<any[]>json).map(Case.fromJson)));
    }

    public getCasesForCurrentUserByYear(yearId: number): Observable<Case[]> {
        return this.http.get(this.appSettings.APIURL + `/program-review/case/current_user/${yearId}`)
        .pipe(map(json => (json["fields"]).map(Case.fromJson)));
    }

    public getDirectReportsForCurrentUser(): Observable<SupervisorDirectReports[]> {
        return this.http.get(this.appSettings.APIURL + '/program-review/direct_reports/current_user')
        .pipe(map(json => (<any[]>json).map(SupervisorDirectReports.fromJson)));
    }

    public getSupervisorsForCurrentUser(): Observable<Supervisor[]> {
        return this.http.get(this.appSettings.APIURL + '/program-review/supervisors/current_user')
        .pipe(map(json => (<any[]>json).map(Supervisor.fromJson)));
    }

    public getNewsStoriesForCurrentUser(yearId: number): Observable<NewsStory[]>{
        return this.http.get(this.appSettings.APIURL + `/program-review/case/current_user/${yearId}/news`)
        .pipe(map(json => (<any[]>json).map(NewsStory.fromJson)));
    }

    public subscribeToNotifications(): void{
        this.authSvc.currentIdentity
        .pipe(map(ci => ci.map(ci => ci.token)))
        .subscribe(token => 
            token.matchDo(
                () => {
                    this.unsubscribeToNotifications();
                },
                t => {
                    this.connect(t);
                }));

    }
    public get cases(): Observable<Case[]> {
        return this.caseSubject.asObservable();
    }
    public get newsStories(): Observable<NewsStory[]> {
        return this.newsSubject.asObservable();
    }
    public newsStoriesByCaseId(caseId: string): Observable<NewsStory[]>{
        return this._caseNewsSubjects.get(caseId);
    }

    private connect(token: string){
        if (this._signalRConnection) {
            return;
        }

        let loadNewsStories = () => {
            this.http.get(this.appSettings.APIURL + '/program-review/news')
            .pipe(map(json => (<any[]>json).map(NewsStory.fromJson)))
            .subscribe(stories => 
            {
                this.newsSubject.next(stories);

                var academicStoryPairs = 
                    stories
                    .map(s => {
                        let academicId =
                            s.elements
                            .map(e => e.case == 'Academic' ? (<NewsStoryElementAcademic>e).academicId : null)
                            .filter(academicId => !!academicId)[0];
                        return [academicId, s] as ([string,NewsStory]);
                    });

                Lookup.fromArray(
                    academicStoryPairs, 
                    ([academicId]) => academicId,
                    ([,story]) => story)
                .forEach((academicId,stories) => 
                    this._caseNewsSubjects.next(academicId, stories));
            });
        }

        let loadCases = () =>
            this.http.get(this.appSettings.APIURL + '/program-review/case')
            .pipe(map(json => (<any[]>json).map(Case.fromJson)))
            .subscribe(cases => this.caseSubject.next(cases));

        let refetchAll = () => {
            loadCases();
            loadNewsStories();
        };

        let options = {accessTokenFactory: () => Promise.resolve(token)};
        // Exponential backoff, capped at 30s, never gives up: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...
        // The inner Math.min keeps the exponent bounded so Math.pow stays finite on long outages.
        let reconnectPolicy: signalR.IRetryPolicy = {
            nextRetryDelayInMilliseconds: ctx =>
                Math.min(30000, 1000 * Math.pow(2, Math.min(ctx.previousRetryCount, 5)))
        };
        let connection = new signalR.HubConnectionBuilder()
            .withUrl(this.appSettings.APIURL + '/program-review/hub', options)
            .withAutomaticReconnect(reconnectPolicy)
            .configureLogging(signalR.LogLevel.Information)
            .build();

        connection.on('caseUpdated', (caseId: string) => {
            this.console.log('Case Updated: ' + caseId);
            loadCases();
        });
        connection.on('casesRebuilt', () => {
            this.console.log('Cases Rebuilt');
            loadCases();
        });
        connection.on('caseNewsUpdated', (caseId: string) => {
            this.console.log('Case News Updated: ' + caseId);
            loadNewsStories();
        });
        connection.on('caseNewsRebuilt', () => {
            this.console.log('Case News Rebuilt');
            loadNewsStories();
        });
        connection.on('systemClockUpdated', (simulatedDate: string | null) => {
            this.console.log('System Clock Updated: ' + simulatedDate);
            // The simulated date lives on the once-fetched /version payload that
            // backs the status banner. Patch it in place (inside the Angular zone
            // so the banner's getter re-evaluates) instead of re-fetching /version.
            this.ngZone.run(() => {
                if (this.appVersion.serverInfo)
                    this.appVersion.serverInfo.simulatedDate = simulatedDate || undefined;
                
                this.appVersion.notifyClockChanged();
            });
        });

        connection.onreconnecting(err =>
            this.console.warn('SignalR reconnecting:', err));
        connection.onreconnected(_ => {
            this.console.info('SignalR reconnected — refetching dashboard state');
            refetchAll();
        });
        connection.onclose(err =>
            this.console.warn('SignalR connection closed:', err));

        let startConnection = () => connection.start().then(_ => {
            this.console.info('Connected to SignalR:', connection);
            connection.invoke('echo','Testing 123...').then(result => this.console.log("Response from SignalR.ProgramReviewHub.echo: ", result));
        });

        startConnection().catch(err =>
            this.console.warn('SignalR initial start failed; watchdog will retry:', err));

        this._signalRConnection = connection;

        this._signalRWatchdog = interval(30000).subscribe(() => {
            if (connection.state === signalR.HubConnectionState.Disconnected) {
                this.console.warn('SignalR watchdog: connection is Disconnected, restarting');
                startConnection()
                    .then(() => refetchAll())
                    .catch(err => this.console.warn('SignalR watchdog restart failed:', err));
            }
        });

        loadCases();
        loadNewsStories();
    }

    public unsubscribeToNotifications(){
        if (this._signalRWatchdog) {
            this._signalRWatchdog.unsubscribe();
            this._signalRWatchdog = null;
        }
        if(!this._signalRConnection){
            return;
        }
        this._signalRConnection.stop();
        this._signalRConnection = null;
    }

    public getCampuses(): Promise<PayrollCampus[]> {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + '/hr/campus'))
        .then(json => (<any[]>json).map(PayrollCampus.fromJson));
    }

    public getAllAcademics(): Promise<HRAcademic[]>
    {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + '/hr/academic'))
        .then(json => (<any[]>json).map(HRAcademic.fromJson));
    }

    public getAllANRUnits(): Promise<HRUnit[]>
    {
        return firstValueFrom(this.http.get(this.appSettings.APIURL + '/hr/anr_units'))
        .then(json => (<any[]>json).map(HRUnit.fromJson));
    }

    public getAllTitlesRanksAndSteps(): Title[]
    {
        return this._titles;
    };

    public execute(cmd: HRCommand): Promise<Result<null,string>>
    {
        return firstValueFrom(cmd.match<Observable<any>>({
            ChangeStep:     (c: ChangeStep)     => this.http.put(this.appSettings.APIURL + '/hr/step'       , c),
            ChangeTerm:     (c: ChangeTerm)     => this.http.put(this.appSettings.APIURL + '/hr/term'       , c),
            SetSupervisors: (c: SetSupervisors) => this.http.put(this.appSettings.APIURL + '/hr/supervisors', c),
            SetAcademic:    (c: SetAcademic)    => this.http.put(this.appSettings.APIURL + '/hr/academic'   , c),
            CreateAcademic: (c: CreateAcademic) => this.http.post(this.appSettings.APIURL + '/hr/academic'  , c)
        }))
        .then(json => Result.fromJson<null, string>(json, _ => null, msg => msg));
    };

    public sendToCandidate(caseId: string): Promise<Result<void,string>> {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/send-to-candidate', {}))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err))
    }

    public recallFromCandidate(caseId: string, reason: string): Promise<Result<void,string>>
    {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/recall', {reason}))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err))
    }

    public proposeDeferral(caseId: string, reason: string): Promise<Result<void,string>>
    {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/propose-deferral', {reason}))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err))
    }

    public approveDeferral(caseId: string): Promise<Result<void,string>>
    {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/approve-deferral', {}))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err))
    }

    public proposeTermReview(caseId: string): Promise<Result<void,string>>
    {
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/propose-term-review', {}))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err))
    }

    public proposeAction(caseId: string, action: ActionProposal): Promise<Result<void,string>>{
        let data = action.toJsonFsharp();
        return firstValueFrom(this.http.post(this.appSettings.APIURL + '/program-review/case/' + caseId + '/proposal', data))
        .then(json => Result.fromJsonFSharp(json, _ => null, err => err));
    }

    public updateUserSeparationDate(c: Case): Promise<Result<void, string>>{
        return firstValueFrom(this.http.post(this.appSettings.APIURL + `/program-review/academic/${c.id}/updateSeparationDate`, {userId: c.userId, separationDate: c.userSeparationDate}))
            .then(json => Result.fromJsonFSharp(json, _ => null, err => err));
    }

    public downloadAcademicExcel() : void {
        
        this.http.get(this.appSettings.APIURL + '/hr/academic_excel_export', {responseType: 'blob'})
        .subscribe({
            next: blob => {
                this.downloadViaDomClick(window.URL.createObjectURL(blob), `AcademicReport.xlsx`);
            },
            error: err => {
                let r = new FileReader();
                r.onloadend = _ => alert('Unable to download usage report: ' + r.result);
                r.readAsText(err.error);
            }
        });

    }
    public downloadCaseReportExcel() : void {

        this.http.get(this.appSettings.APIURL + '/reports/case_Report', {responseType: 'blob'})
        .subscribe({
            next: blob => {
                this.downloadViaDomClick(window.URL.createObjectURL(blob), `CaseReport.xlsx`);
            },
            error: err => {
                let r = new FileReader();
                r.onloadend = _ => alert('Unable to download case report: ' + r.result);
                r.readAsText(err.error);
            }
        });
    }

    public downloadFileDownloadEventExcel() : void {
        
        this.http.get(this.appSettings.APIURL + '/reports/fileDownloadEvent_Report', {responseType: 'blob'})
        .subscribe({
            next: blob => {
                this.downloadViaDomClick(window.URL.createObjectURL(blob), `FileDownloadEvent.xlsx`);
            },
            error: err => {
                let r = new FileReader();
                r.onloadend = _ => alert('Unable to download File Download Event report: ' + r.result);
                r.readAsText(err.error);
            }
        });
    }

    public downloadEligibilityListExcel(rows: EligibilityListReportRow[]) : void {
        this.http.post(this.appSettings.APIURL + '/reports/eligibilityList_Report', rows, {responseType: 'blob'})
        .subscribe({
            next: blob => {
                this.downloadViaDomClick(window.URL.createObjectURL(blob), `EligibilityList.xlsx`);
            },
            error: err => {
                let r = new FileReader();
                r.onloadend = _ => alert('Unable to download Eligibility List report: ' + r.result);
                r.readAsText(err.error);
            }
        });
    }

    public downloadViaDomClick(url: string, filename: string){
        let anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = filename;

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);        
    }
    
}